import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Player, PlayerRef } from '@remotion/player';
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import styled, { keyframes } from 'styled-components';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Gap from '@component/common/gap';
import TextBlock from '@component/common/block/text-block';
import type { IPlaybackState } from '@component/common/otter-teacher';
import type {
  IClassroomExpressionScene,
  IClassroomExpressionWatch,
  IClassroomGrammarWatchContent,
  IClassroomGrammarWatchConstruction,
  IClassroomGrammarWatchExpression,
  IClassroomGrammarWatchScene,
  IClassroomGrammarWatchSentence,
} from '@datatype/iyagigo/api-type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { normalizeExplanation } from '@util/string';
import { v4 as uuid4 } from 'uuid';

const FPS = 30;
const COMPOSITION_WIDTH = 1280;
const DEFAULT_COMPOSITION_HEIGHT = 720;
const GAP_1 = '20px';
const GAP_2 = '40px';
const GAP_3 = '60px';
const GAP_4 = '80px';
const GAP_5 = '100px';

type TWatchBoardClass = 'grammar' | 'expression' | 'conversation';

type IProps = {
  playback: IPlaybackState;
  contentKey: string;
} & (
  | {
      class: 'grammar';
      watch: IClassroomGrammarWatchContent;
      languageCode?: EApiLanguageCode;
    }
  | {
      // 생활회화는 기본표현과 watch 데이터 계약(scene[] + audioLink)이 동일해 renderer를 공유한다.
      class: 'expression' | 'conversation';
      watch: IClassroomExpressionWatch;
      languageCode?: EApiLanguageCode;
    }
);

type ICompositionProps = Record<string, unknown> &
  (
    | {
        class: 'grammar';
        watch: IClassroomGrammarWatchContent;
        contentKey: string;
        languageCode?: EApiLanguageCode;
      }
    | {
        class: 'expression' | 'conversation';
        watch: IClassroomExpressionWatch;
        contentKey: string;
        languageCode?: EApiLanguageCode;
      }
  );

const BOARD_DIRECTION_BY_CONTENT_KEY: Record<
  string,
  Partial<
    Record<
      'intro' | 'concept' | 'rule' | 'note' | 'demonstration' | 'summary',
      Partial<Record<'keyword' | 'item' | 'vocabulary' | 'meaning' | 'expression' | 'construction', 'row' | 'column'>>
    >
  >
> = {
  'i-adj-plain': {
    concept: { item: 'row' },
  },
  'i-adj-desu': {
    summary: { item: 'column' },
  },
  'na-adj-plain': {
    summary: { item: 'column' },
  },
  'na-adj-desu': {
    summary: { item: 'column' },
  },
  'na-adj-caution': {
    intro: { keyword: 'row' },
    summary: { item: 'column' },
  },
  'na-adj-past': {
    summary: { item: 'row' },
  },
  'demonstrative-kore': {
    intro: { keyword: 'row' },
    rule: { keyword: 'row' },
  },
  'demonstrative-kono': {
    intro: { keyword: 'row' },
    rule: { keyword: 'row' },
  },
  'noun-plain': {
    concept: { item: 'row' },
    summary: { item: 'column' },
  },
  'noun-past-negative': {
    concept: { meaning: 'column' },
  },
  'na-adj-past-negative': {
    concept: { meaning: 'column' },
  },
  'noun-desu': {
    concept: { item: 'column' },
  },
  'verb-type-basic': {
    intro: { keyword: 'row' },
    concept: { item: 'column', vocabulary: 'column' },
    note: { expression: 'row' },
  },
  'irregular-plain': {
    demonstration: { construction: 'row' },
  },
};

const getBoardDirection = (
  contentKey: string,
  phase: 'intro' | 'concept' | 'rule' | 'note' | 'demonstration' | 'summary',
  target: 'keyword' | 'item' | 'vocabulary' | 'meaning' | 'expression' | 'construction',
  fallback: 'row' | 'column',
) => BOARD_DIRECTION_BY_CONTENT_KEY[contentKey]?.[phase]?.[target] ?? fallback;

const chalkboardBackground = `
  radial-gradient(circle at 52% 45%, rgba(79, 126, 102, 0.5), transparent 62%),
  linear-gradient(135deg, #315f4e 0%, #23483c 100%)
`;
// background: #254b3d;

const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  background: ${chalkboardBackground};
`;

// 1280px 너비 좌표계에서 사용하는 칠판 폰트 스케일.
const BOARD_FONT = {
  small: 52, // table meaning
  medium: 70, // description, meaning, idle 안내
  large: 85, // subject, vocabulary·sentence 본문, formula from/연산자
  xlarge: 90, // keyword(제목), formula 결과(to)
} as const;

const BOARD_COLOR = {
  subject: '#f3d88f',
  description: '#dcebd7',
  keyword: '#fff9df',
  item: '#fff5cf',
  itemUnderline: '#f3a3b6',
  itemReading: '#c9ddc8',
  itemMeaning: '#dcebd7',
  itemAccent: '#f2c66d',
  itemResult: '#ffe49a',
  idle: '#f4e7bd',
} as const;

const BOARD_TEXT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'giant' as const,
    custom: { fontSize: BOARD_FONT.large },
    color: BOARD_COLOR.item,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_TABLE_TEXT_DESIGN = {
  box: {
    alignX: 'center' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.item,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_TABLE_MEANING_DESIGN = {
  box: {
    alignX: 'center' as const,
  },
  text: {
    size: 'medium' as const,
    custom: { fontSize: BOARD_FONT.small },
    color: BOARD_COLOR.itemMeaning,
  },
};

const BOARD_RESULT_TEXT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'giant' as const,
    custom: { fontSize: BOARD_FONT.large },
    color: BOARD_COLOR.itemResult,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_ITEM_TEXT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.item,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_ITEM_LABEL_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.itemAccent,
    oneline: true,
  },
};

const BOARD_SUBJECT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'giant' as const,
    custom: { fontSize: BOARD_FONT.large },
    color: BOARD_COLOR.subject,
  },
};

const BOARD_DESCRIPTION_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.description,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_KEYWORD_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'giant' as const,
    custom: { fontSize: BOARD_FONT.xlarge },
    color: BOARD_COLOR.keyword,
  },
};

const BOARD_MEANING_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.itemMeaning,
    underlineColor: BOARD_COLOR.itemUnderline,
  },
};

const BOARD_LARGE_ACCENT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'giant' as const,
    custom: { fontSize: BOARD_FONT.large },
    color: BOARD_COLOR.itemAccent,
  },
};

const BOARD_MEDIUM_ACCENT_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.itemAccent,
  },
};

const BOARD_IDLE_DESIGN = {
  box: {
    alignX: 'left' as const,
  },
  text: {
    size: 'huge' as const,
    custom: { fontSize: BOARD_FONT.medium },
    color: BOARD_COLOR.idle,
  },
};

const ConstructionsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  gap: ${GAP_2};
`;

const ConstructionContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  flex-flow: ${({ $direction }) => `${$direction} nowrap`};
  width: 100%;
  align-items: ${({ $direction }) => ($direction === 'row' ? 'center' : 'flex-start')};
  justify-content: flex-start;
  gap: ${({ $direction }) => ($direction === 'row' ? GAP_2 : '0')};
`;

const ConstructionFrom = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-end;
`;

const ConstructionResult = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const SentenceContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const ConceptItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  gap: ${GAP_2};
`;

const KeywordsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${GAP_2};
`;

const KeywordContainer = styled.div<{ $direction: 'row' | 'column'; $hasReading: boolean }>`
  display: flex;
  width: 100%;
  flex-flow: ${({ $direction }) => $direction} nowrap;
  align-items: ${({ $direction }) => ($direction === 'row' ? 'flex-end' : 'flex-start')};
  justify-content: flex-start;
  gap: ${({ $direction }) => ($direction === 'row' ? GAP_2 : GAP_1)};

  & > :first-child {
    transform: ${({ $direction, $hasReading }) => ($direction === 'row' && $hasReading ? 'translateY(-10px)' : 'none')};
  }
`;

const ConceptItemContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  width: 100%;
  flex-flow: ${({ $direction }) => $direction} nowrap;
  align-items: flex-start;
  gap: ${({ $direction }) => ($direction === 'column' ? GAP_1 : GAP_2)};
`;

const ConceptVocabularyItemContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  width: 100%;
  flex-flow: ${({ $direction }) => $direction} nowrap;
  align-items: ${({ $direction }) => ($direction === 'row' ? 'flex-end' : 'flex-start')};
`;

const ConceptItemVocabulariesContainer = styled.div<{ $direction: 'row' | 'column'; $stackMeaning: boolean }>`
  display: flex;
  width: ${({ $stackMeaning }) => ($stackMeaning ? '100%' : 'auto')};
  flex-flow: ${({ $direction }) => `${$direction} ${$direction === 'row' ? 'wrap' : 'nowrap'}`};
  // iOS WebView는 후리가나 TextBlock(2행 중첩 flex)의 baseline 합성이 불안정해
  // reading이 base 옆으로 튀어나온다. base 밑선 기준(flex-end)으로 정렬해 회피.
  align-items: flex-end;
  gap: ${GAP_2};
`;

const ConceptItemVocabularyContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  width: ${({ $direction }) => ($direction === 'column' ? '100%' : 'auto')};
  flex-flow: ${({ $direction }) => $direction} nowrap;
  align-items: ${({ $direction }) => ($direction === 'column' ? 'flex-start' : 'flex-end')};
`;

const SummaryItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  gap: ${GAP_2};
`;

const SummaryItemContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  width: 100%;
  flex-flow: ${({ $direction }) => $direction} nowrap;
  align-items: flex-start;
  gap: ${({ $direction }) => ($direction === 'column' ? GAP_1 : GAP_2)};
`;

const IntroPhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const ConceptPhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const RulePhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const DemonstrationPhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const ClassroomExpressionItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${GAP_2};
`;

const ClassroomExpressionItemContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const ClassroomExpressionTableContainer = styled.div<{ $column: number; $row: number }>`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(${({ $column }) => $column}, minmax(0, 1fr));
  grid-template-rows: repeat(${({ $row }) => $row}, auto);
  align-items: start;
  border-top: 1px solid rgba(255, 244, 207, 0.45);
  border-left: 1px solid rgba(255, 244, 207, 0.45);
`;

const ClassroomExpressionTableItem = styled.div`
  display: flex;
  min-width: 0;
  flex-flow: column nowrap;
  align-items: center;
  padding: ${GAP_1};
  border-right: 1px solid rgba(255, 244, 207, 0.45);
  border-bottom: 1px solid rgba(255, 244, 207, 0.45);
`;

const ClassroomExpressionPhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const ExamplePhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const NotePhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const NoteExpressionsContainer = styled.div<{ $direction: 'row' | 'column' }>`
  display: flex;
  flex-flow: ${({ $direction }) => `${$direction} nowrap`};
  align-items: ${({ $direction }) => ($direction === 'row' ? 'flex-end' : 'flex-start')};
  gap: ${({ $direction }) => ($direction === 'row' ? GAP_1 : GAP_2)};
`;

const NoteExpressionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const NoteExpressionSeparator = styled.span`
  align-self: flex-end;
  color: ${BOARD_COLOR.item};
  font-size: ${BOARD_FONT.medium}px;
  line-height: 1;
`;

const SummaryPhaseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const SceneFrame = styled(AbsoluteFill)<{ $opacity: number; $transform: string }>`
  padding: 50px;
  opacity: ${({ $opacity }) => $opacity};
  transform: ${({ $transform }) => $transform};
`;

const SceneViewport = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const SceneContent = styled.div<{ $offset: number }>`
  width: 100%;
  transform: translateY(-${({ $offset }) => $offset}px);
  transition: transform 180ms ease-out;
`;

const ScenePageControls = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  z-index: 1;
`;

const scenePagePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 228, 154, 0.15);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 0 14px rgba(255, 228, 154, 0.28);
  }
`;

const ScenePageButton = styled.button<{ $emphasized?: boolean }>`
  display: flex;
  width: 96px;
  height: 96px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px solid rgba(255, 249, 223, 0.7);
  border-radius: 50%;
  color: ${BOARD_COLOR.keyword};
  background: rgba(24, 58, 47, 0.88);
  cursor: pointer;
  animation: ${({ $emphasized }) => ($emphasized ? scenePagePulse : 'none')} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Chalkboard = styled(AbsoluteFill)`
  overflow: hidden;
  color: ${BOARD_COLOR.keyword};
  background: transparent;
  font-family: 'Pretendard Variable', 'Noto Sans KR', sans-serif;
`;

const IdleMessageContainer = styled.div<{ $opacity: number }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  opacity: ${({ $opacity }) => $opacity};
`;

// 판서 등장 애니메이션(아래에서 살짝 떠오르며 나타남)
const useAppear = (delay: number) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const localFrame = Math.max(0, frame - delay);
  const progress = spring({ frame: localFrame, fps, config: { damping: 18, stiffness: 120, mass: 0.7 } });

  return {
    opacity: interpolate(progress, [0, 1], [0, 1]),
    transform: `translateY(${interpolate(progress, [0, 1], [18, 0])}px)`,
  };
};

// 예문 문장(일본어 + 뜻)
const Sentence: React.FC<{
  sentence: IClassroomGrammarWatchSentence;
}> = ({ sentence }) => (
  <SentenceContainer>
    <TextBlock
      text={sentence.text}
      annotation={
        sentence.pronunciation ? { reading: sentence.pronunciation, color: BOARD_COLOR.itemReading } : undefined
      }
      annotationLayout='center'
      design={BOARD_TEXT_DESIGN}
    />
    {sentence.translation && (
      <>
        <Gap height={GAP_1} />
        <TextBlock text={sentence.translation} design={BOARD_MEANING_DESIGN} />
      </>
    )}
  </SentenceContainer>
);

const Expression: React.FC<{
  expression: IClassroomGrammarWatchExpression;
}> = ({ expression }) => (
  <>
    <TextBlock
      text={expression.text}
      annotation={
        expression.pronunciation
          ? {
              reading: expression.pronunciation,
              color: BOARD_COLOR.itemReading,
            }
          : undefined
      }
      annotationLayout='center'
      design={BOARD_TEXT_DESIGN}
    />
    {expression.translation && <TextBlock text={expression.translation} design={BOARD_MEANING_DESIGN} />}
  </>
);

const ClassroomExpression: React.FC<{
  expression: IClassroomExpressionScene['expression'][number];
  showPronunciation?: boolean;
}> = ({ expression, showPronunciation = true }) => (
  <>
    <TextBlock
      text={expression.notation}
      annotation={
        expression.pronunciation && showPronunciation
          ? {
              reading: expression.pronunciation,
              color: BOARD_COLOR.itemReading,
            }
          : undefined
      }
      annotationLayout='center'
      design={BOARD_TEXT_DESIGN}
    />
    {expression.translation?.ko && <TextBlock text={expression.translation.ko} design={BOARD_MEANING_DESIGN} />}
  </>
);

const ClassroomExpressionTable: React.FC<{
  table: NonNullable<IClassroomExpressionScene['table']>;
  showPronunciation?: boolean;
}> = ({ table, showPronunciation = true }) => {
  const Items = table.item.map((item) => (
    <ClassroomExpressionTableItem key={uuid4()}>
      <TextBlock
        text={item.notation}
        annotation={
          item.pronunciation && showPronunciation
            ? { reading: item.pronunciation, color: BOARD_COLOR.itemReading }
            : undefined
        }
        annotationLayout='center'
        design={BOARD_TABLE_TEXT_DESIGN}
      />
      {item.translation?.ko && <TextBlock text={item.translation.ko} design={BOARD_TABLE_MEANING_DESIGN} />}
    </ClassroomExpressionTableItem>
  ));

  return (
    <ClassroomExpressionTableContainer $row={table.row} $column={table.column}>
      {Items}
    </ClassroomExpressionTableContainer>
  );
};

const Constructions: React.FC<{
  construction: IClassroomGrammarWatchConstruction[];
  direction: 'row' | 'column';
}> = ({ construction, direction }) => (
  <>
    {construction.map((value) => {
      const Sources = value.from.map((token, index) => (
        <React.Fragment key={uuid4()}>
          {index > 0 && (
            <>
              <Gap width={GAP_2} />
              <TextBlock text='+' design={BOARD_LARGE_ACCENT_DESIGN} />
              <Gap width={GAP_2} />
            </>
          )}
          <TextBlock text={token} design={BOARD_TEXT_DESIGN} />
        </React.Fragment>
      ));

      return (
        <ConstructionContainer key={uuid4()} $direction={direction}>
          <ConstructionFrom>{Sources}</ConstructionFrom>
          <TextBlock text={direction === 'row' ? '→' : '↓'} design={BOARD_MEDIUM_ACCENT_DESIGN} />
          {direction === 'row' ? <Gap width={GAP_2} /> : <Gap height={GAP_2} />}
          <ConstructionResult>
            <TextBlock
              text={value.to}
              annotation={{ reading: value.pronunciation, color: BOARD_COLOR.itemReading }}
              annotationLayout='center'
              design={BOARD_RESULT_TEXT_DESIGN}
            />
            <TextBlock text={value.translation} design={BOARD_MEANING_DESIGN} />
          </ConstructionResult>
        </ConstructionContainer>
      );
    })}
  </>
);

interface IPhaseProps {
  scene: IClassroomGrammarWatchScene;
}

const Keywords: React.FC<Pick<IClassroomGrammarWatchScene, 'keyword'> & { itemDirection?: 'row' | 'column' }> = ({
  keyword,
  itemDirection = 'column',
}) => (
  <KeywordsContainer data-direction={itemDirection}>
    {(keyword ?? []).map((value) => (
      <KeywordContainer key={uuid4()} $direction={itemDirection} $hasReading={Boolean(value.pronunciation)}>
        {value.label && <TextBlock text={value.label} design={BOARD_ITEM_LABEL_DESIGN} />}
        <TextBlock
          text={value.content}
          annotation={
            value.pronunciation
              ? {
                  reading: value.pronunciation,
                  color: BOARD_COLOR.itemReading,
                }
              : undefined
          }
          annotationLayout='center'
          design={BOARD_KEYWORD_DESIGN}
        />
      </KeywordContainer>
    ))}
  </KeywordsContainer>
);

const IntroPhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const itemDirection = getBoardDirection(contentKey, 'intro', 'keyword', 'column');

  return (
    <IntroPhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.keyword && scene.keyword.length > 0 && <Keywords keyword={scene.keyword} itemDirection={itemDirection} />}
    </IntroPhaseContainer>
  );
};

const ConceptPhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const vocabularyDirection = getBoardDirection(contentKey, 'concept', 'vocabulary', 'row');
  const meaningDirection = getBoardDirection(contentKey, 'concept', 'meaning', 'row');
  const itemDirection = getBoardDirection(contentKey, 'concept', 'item', 'column');
  const contentDirection = getBoardDirection(contentKey, 'concept', 'item', 'row');
  const ConceptItems = (scene.item ?? []).map((item) => {
    const ConceptItemVocabularies = (item.vocabulary ?? []).map((vocabulary) => (
      <ConceptItemVocabularyContainer key={uuid4()} $direction={meaningDirection}>
        <TextBlock
          text={vocabulary.keyword}
          annotation={
            vocabulary.pronunciation
              ? {
                  reading: vocabulary.pronunciation,
                  color: BOARD_COLOR.itemReading,
                }
              : undefined
          }
          annotationLayout='center'
          design={BOARD_ITEM_TEXT_DESIGN}
        />
        {vocabulary.meaning && (
          <>
            {meaningDirection === 'column' ? <Gap height={GAP_1} /> : <Gap width={GAP_2} />}
            <TextBlock text={vocabulary.meaning} design={BOARD_MEANING_DESIGN} />
          </>
        )}
      </ConceptItemVocabularyContainer>
    ));

    if (ConceptItemVocabularies.length > 0) {
      return (
        <ConceptVocabularyItemContainer key={uuid4()} $direction={itemDirection}>
          {item.label && (
            <>
              <TextBlock text={item.label} design={BOARD_ITEM_LABEL_DESIGN} />
              {itemDirection === 'row' ? <Gap width={GAP_2} /> : <Gap height={GAP_1} />}
            </>
          )}
          {item.content && (
            <>
              <TextBlock text={item.content} design={BOARD_ITEM_TEXT_DESIGN} />
              <Gap height={GAP_2} />
            </>
          )}
          <ConceptItemVocabulariesContainer $direction={vocabularyDirection} $stackMeaning={meaningDirection === 'column'}>
            {ConceptItemVocabularies}
          </ConceptItemVocabulariesContainer>
        </ConceptVocabularyItemContainer>
      );
    }

    return (
      <ConceptItemContainer key={uuid4()} $direction={contentDirection}>
        <TextBlock text={item.label} design={BOARD_ITEM_LABEL_DESIGN} />
        {item.content && <TextBlock text={item.content} design={BOARD_ITEM_TEXT_DESIGN} />}
      </ConceptItemContainer>
    );
  });

  return (
    <ConceptPhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {ConceptItems.length > 0 && <ConceptItemsContainer>{ConceptItems}</ConceptItemsContainer>}
    </ConceptPhaseContainer>
  );
};

const RulePhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const itemDirection = getBoardDirection(contentKey, 'rule', 'keyword', 'column');
  return (
    <RulePhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_5} />
        </>
      )}
      {scene.keyword && scene.keyword.length > 0 && (
        <>
          <Keywords keyword={scene.keyword} itemDirection={itemDirection} />
          <Gap height={GAP_5} />
        </>
      )}
    </RulePhaseContainer>
  );
};

const DemonstrationPhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const constructionDirection = getBoardDirection(contentKey, 'demonstration', 'construction', 'column');

  return (
    <DemonstrationPhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.construction && scene.construction.length > 0 && (
        <ConstructionsContainer>
          <Constructions construction={scene.construction} direction={constructionDirection} />
        </ConstructionsContainer>
      )}
    </DemonstrationPhaseContainer>
  );
};

const ExamplePhase: React.FC<IPhaseProps> = ({ scene }) => {
  return (
    <ExamplePhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.sentence && <Sentence sentence={scene.sentence} />}
    </ExamplePhaseContainer>
  );
};

const NotePhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const expressionDirection = getBoardDirection(contentKey, 'note', 'expression', 'column');
  const Expressions = (scene.expression ?? []).flatMap((expression, index) => [
    ...(index > 0 && expressionDirection === 'row'
      ? [<NoteExpressionSeparator key={`separator-${index}`}>·</NoteExpressionSeparator>]
      : []),
    <NoteExpressionContainer key={uuid4()}>
      <Expression expression={expression} />
    </NoteExpressionContainer>,
  ]);

  return (
    <NotePhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {Expressions.length > 0 && (
        <NoteExpressionsContainer $direction={expressionDirection}>{Expressions}</NoteExpressionsContainer>
      )}
    </NotePhaseContainer>
  );
};

const SummaryPhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  const itemDirection = getBoardDirection(contentKey, 'summary', 'item', 'row');
  const Items = (scene.item ?? []).map((item) => (
    <SummaryItemContainer key={uuid4()} $direction={itemDirection}>
      <TextBlock text={item.label} design={BOARD_ITEM_LABEL_DESIGN} />
      {item.content && <TextBlock text={item.content} design={BOARD_ITEM_TEXT_DESIGN} />}
    </SummaryItemContainer>
  ));

  return (
    <SummaryPhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {Items.length > 0 && <SummaryItemsContainer>{Items}</SummaryItemsContainer>}
    </SummaryPhaseContainer>
  );
};

const GrammarScenePhase: React.FC<IPhaseProps & { contentKey: string }> = ({ scene, contentKey }) => {
  switch (scene.phase) {
    case 'intro':
      return <IntroPhase scene={scene} contentKey={contentKey} />;
    case 'concept':
      return <ConceptPhase scene={scene} contentKey={contentKey} />;
    case 'rule':
      return <RulePhase scene={scene} contentKey={contentKey} />;
    case 'demonstration':
      return <DemonstrationPhase scene={scene} contentKey={contentKey} />;
    case 'example':
      return <ExamplePhase scene={scene} />;
    case 'note':
      return <NotePhase scene={scene} contentKey={contentKey} />;
    case 'summary':
      return <SummaryPhase scene={scene} contentKey={contentKey} />;
    default:
      return null;
  }
};

const ClassroomExpressionScenePhase: React.FC<{
  scene: IClassroomExpressionScene;
  languageCode?: EApiLanguageCode;
}> = ({ scene, languageCode }) => {
  const showPronunciation = languageCode !== EApiLanguageCode.ENGLISH;
  const Expressions = scene.expression.map((expression) => (
    <ClassroomExpressionItemContainer key={uuid4()}>
      <ClassroomExpression expression={expression} showPronunciation={showPronunciation} />
    </ClassroomExpressionItemContainer>
  ));

  return (
    <ClassroomExpressionPhaseContainer>
      {scene.subject && (
        <>
          <TextBlock text={scene.subject} design={BOARD_SUBJECT_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.description && (
        <>
          <TextBlock text={normalizeExplanation(scene.description, 'inherit')} design={BOARD_DESCRIPTION_DESIGN} />
          <Gap height={GAP_2} />
        </>
      )}
      {scene.table && <ClassroomExpressionTable table={scene.table} showPronunciation={showPronunciation} />}
      {Expressions.length > 0 && <ClassroomExpressionItemsContainer>{Expressions}</ClassroomExpressionItemsContainer>}
    </ClassroomExpressionPhaseContainer>
  );
};

const ScenePhase: React.FC<{
  class: TWatchBoardClass;
  scene: IClassroomGrammarWatchScene | IClassroomExpressionScene;
  contentKey: string;
  languageCode?: EApiLanguageCode;
}> = ({ class: classroomClass, scene, contentKey, languageCode }) => {
  if (classroomClass === 'expression' || classroomClass === 'conversation') {
    return <ClassroomExpressionScenePhase scene={scene as IClassroomExpressionScene} languageCode={languageCode} />;
  }

  return <GrammarScenePhase scene={scene as IClassroomGrammarWatchScene} contentKey={contentKey} />;
};

const PaginatedSceneContent: React.FC<React.PropsWithChildren> = ({ children }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);

  const updateOverflow = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const nextMaxOffset = Math.max(0, content.scrollHeight - viewport.clientHeight);
    setMaxOffset(nextMaxOffset);
    setOffset((current) => Math.min(current, nextMaxOffset));
  }, []);

  useEffect(() => {
    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    if (viewportRef.current) observer.observe(viewportRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [updateOverflow]);

  const movePage = (direction: -1 | 1) => {
    const viewportHeight = viewportRef.current?.clientHeight ?? 0;
    const pageDistance = Math.max(1, Math.round(viewportHeight * 0.72));
    setOffset((current) => Math.min(maxOffset, Math.max(0, current + direction * pageDistance)));
  };

  return (
    <SceneViewport ref={viewportRef}>
      <SceneContent ref={contentRef} $offset={offset}>
        {children}
      </SceneContent>
      {maxOffset > 0 && (
        <ScenePageControls>
          {offset > 0 && (
            <ScenePageButton
              type='button'
              aria-label='판서 위쪽 보기'
              onClick={(event) => {
                event.stopPropagation();
                movePage(-1);
              }}>
              <ChevronUp size={60} strokeWidth={2.5} />
            </ScenePageButton>
          )}
          {offset < maxOffset && (
            <ScenePageButton
              $emphasized
              type='button'
              aria-label='판서 아래쪽 보기'
              onClick={(event) => {
                event.stopPropagation();
                movePage(1);
              }}>
              <ChevronDown size={60} strokeWidth={2.5} />
            </ScenePageButton>
          )}
        </ScenePageControls>
      )}
    </SceneViewport>
  );
};

// 한 scene(장면) — at 시점부터 다음 scene 직전까지 화면 중앙에 홀로 표시
const SceneView: React.FC<{
  class: TWatchBoardClass;
  scene: IClassroomGrammarWatchScene | IClassroomExpressionScene;
  startFrame: number;
  endFrame: number;
  isLast: boolean;
  contentKey: string;
  languageCode?: EApiLanguageCode;
}> = ({ class: classroomClass, scene, startFrame, endFrame, isLast, contentKey, languageCode }) => {
  const frame = useCurrentFrame();
  const appear = useAppear(startFrame);
  // 다음 scene으로 넘어가기 직전 8프레임 동안 페이드아웃. 마지막 scene은 전환이 없으니 끝까지 유지(잔상 방지).
  const fadeOut = isLast
    ? 1
    : interpolate(frame, [endFrame - 8, endFrame], [1, 0], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
  if (frame < startFrame || frame >= endFrame) return null;

  return (
    <SceneFrame $opacity={appear.opacity * fadeOut} $transform={appear.transform}>
      <PaginatedSceneContent>
        <ScenePhase class={classroomClass} scene={scene} contentKey={contentKey} languageCode={languageCode} />
      </PaginatedSceneContent>
    </SceneFrame>
  );
};

// at(ms) → frame. at은 밀리초, fps는 초당 프레임이라 1000으로 나눈다.
const msToFrame = (ms: number, fps: number) => Math.round((ms * fps) / 1000);

const ChalkboardComposition: React.FC<ICompositionProps> = ({
  class: classroomClass,
  watch,
  contentKey,
  languageCode,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const scenes: (IClassroomGrammarWatchScene | IClassroomExpressionScene)[] = watch.scene;
  const firstFrame = msToFrame(scenes[0]?.at ?? 0, fps);
  // 첫 scene 등장 전 idle 안내 문구
  const idleOpacity = interpolate(frame, [firstFrame, firstFrame + 8], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const Scenes = scenes.map((scene, index) => {
    const isLast = index === scenes.length - 1;
    const startFrame = msToFrame(scene.at ?? 0, fps);
    const endFrame = isLast ? durationInFrames : msToFrame(scenes[index + 1].at ?? 0, fps);
    return (
      <SceneView
        key={index}
        class={classroomClass}
        scene={scene}
        startFrame={startFrame}
        endFrame={endFrame}
        isLast={isLast}
        contentKey={contentKey}
        languageCode={languageCode}
      />
    );
  });

  return (
    <Chalkboard>
      <IdleMessageContainer $opacity={idleOpacity}>
        <TextBlock text='수달 선생님을 누르면 수업이 시작돼요' design={BOARD_IDLE_DESIGN} />
      </IdleMessageContainer>

      {Scenes}
    </Chalkboard>
  );
};

const RemotionWatchBoard: React.FC<IProps> = ({ class: classroomClass, watch, playback, contentKey, languageCode }) => {
  const playerRef = useRef<PlayerRef>(null);
  const [compositionHeight, setCompositionHeight] = useState(DEFAULT_COMPOSITION_HEIGHT);
  // playback.duration(초, HTML audio 실측) 기준. 오디오 메타데이터 로드 전엔 0이라 최소 1프레임 보장.
  const durationInFrames = Math.max(1, Math.ceil(playback.duration * FPS));

  useEffect(() => {
    const player = playerRef.current?.getContainerNode();
    if (!player) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setCompositionHeight(Math.round((COMPOSITION_WIDTH * height) / width));
      }
    });
    observer.observe(player);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const targetFrame = Math.min(Math.floor(playback.currentTime * FPS), durationInFrames - 1);
    if (Math.abs(player.getCurrentFrame() - targetFrame) > 2) {
      player.seekTo(targetFrame);
    }

    if (playback.isPlaying && !player.isPlaying()) {
      player.play();
    } else if (!playback.isPlaying && player.isPlaying()) {
      player.pause();
    }
  }, [durationInFrames, playback.currentTime, playback.isPlaying]);

  return (
    <Wrapper aria-label='수달 선생님 판서 자료'>
      <Player
        ref={playerRef}
        component={ChalkboardComposition}
        inputProps={{ class: classroomClass, watch, contentKey, languageCode } as ICompositionProps}
        durationInFrames={durationInFrames}
        compositionWidth={COMPOSITION_WIDTH}
        compositionHeight={compositionHeight}
        fps={FPS}
        playbackRate={playback.playbackRate}
        controls={false}
        clickToPlay={false}
        spaceKeyToPlayOrPause={false}
        acknowledgeRemotionLicense
        style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
      />
    </Wrapper>
  );
};

export default RemotionWatchBoard;
